import { useTranslation } from 'react-i18next'

export interface PeriodAffordanceBannerProps {
  /**
   * The resolved billing period (from API meta.default_period).
   * When `source === 'explicit'`, the banner renders nothing (caller explicitly
   * chose a period, so the affordance is not needed).
   */
  period?: {
    from?: string
    to?: string
    source?: 'explicit' | 'billing_cycle' | 'calendar_fallback'
  }
  /** Pre-formatted period label — CALLER must format via useDateFormatter(). Never format inside this component. */
  formattedPeriod: string
  /** Called when the user clicks the "All periods" escape. */
  onShowAllPeriods: () => void
}

/**
 * EP-97: Shown on Invoices + Payments when the list is period-scoped (default
 * latest billing period). Renders nothing when source === 'explicit' (caller
 * chose an explicit period range — no affordance needed).
 *
 * Date-flow rule: NEVER call toLocaleDateString() / toLocaleString() here.
 * The caller (page-level component) formats the period via useDateFormatter()
 * and passes the result as `formattedPeriod`.
 */
export function PeriodAffordanceBanner({
  period,
  formattedPeriod,
  onShowAllPeriods,
}: PeriodAffordanceBannerProps) {
  const { t } = useTranslation('common')

  // Explicit period = caller made a deliberate choice; hide the affordance.
  if (period?.source === 'explicit') return null

  return (
    <div className="flex items-center gap-2 text-sm text-base-content/70 py-1">
      <span>{t('list.showing_period', { period: formattedPeriod })}</span>
      <button
        type="button"
        className="link link-primary text-sm"
        onClick={onShowAllPeriods}
      >
        {t('list.all_periods')}
      </button>
    </div>
  )
}
